import { useEffect, useState } from "react";
import { Outlet, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Sentry } from "@/lib/sentry";

/**
 * Checks if the current environment should bypass authentication.
 * Used in dev/preview (Lovable, webcontainer) to allow access without login.
 */
function isDevEnvironment(): boolean {
  return (
    import.meta.env.VITE_SKIP_AUTH === "true" ||
    window.location.hostname.includes("lovable.app") ||
    window.location.hostname.includes("lovableproject.com") ||
    window.location.hostname.includes("webcontainer")
  );
}

/**
 * Authentication guard rendered as a layout route element.
 * Wraps all protected routes via `<Outlet />`.
 *
 * In dev/preview environments, auth is bypassed automatically.
 * In production, the user is redirected to /login if unauthenticated.
 */
export default function ProtectedRoute() {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const devMode = isDevEnvironment();

  useEffect(() => {
    if (devMode) {
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    function handleSession(session: { user: { id: string; email?: string } } | null) {
      setIsAuthenticated(!!session);
      setIsLoading(false);
      if (session?.user) {
        Sentry.setUser({ id: session.user.id, email: session.user.email });
      } else {
        Sentry.setUser(null);
      }
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => handleSession(session));

    supabase.auth.getSession().then(({ data: { session } }) => handleSession(session));

    return () => subscription.unsubscribe();
  }, [devMode]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

/**
 * Re-export the dev check so Login and other components can use the same logic.
 */
export { isDevEnvironment };
